import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import CreateAdminModal from '../components/CreateAdminModal';
import EditAdminModal from '../components/EditAdminModal';
import {
  UserPlus,
  Search,
  Filter,
  Shield,
  ShieldCheck,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Admin } from '../types';
import { useAuth } from '../context/AuthContext';

const AdminUsersPage: React.FC = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [stats, setStats] = useState({
    total: '0',
    active: '0',
    super_admins: '0',
    admins: '0',
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchAdmins();
    if (isSuperAdmin) {
      fetchStats();
    }
    // eslint-disable-next-line
  }, [roleFilter, statusFilter]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: 1,
        limit: 100,
      };
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';

      const response = await axios.get('/admins', { params });

      const loadedAdmins = response.data?.data || [];
      setAdmins(Array.isArray(loadedAdmins) ? loadedAdmins : []);
    } catch (error: any) {
      console.error('Failed to load admins:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to load admin users');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admins/statistics');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDeleteAdmin = async (adminId: number, adminName: string) => {
    if (adminId === user?.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${adminName}?\n\nThis action cannot be undone.\n\nType 'DELETE' in the next prompt to confirm.`
    );

    if (!confirmed) return;

    const confirmText = window.prompt('Type DELETE to confirm deletion:');
    if (confirmText !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }

    try {
      await axios.delete(`/admins/${adminId}`);
      toast.success(`Admin ${adminName} deleted successfully!`);
      fetchAdmins();
      if (isSuperAdmin) {
        fetchStats();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message || 'Failed to delete admin user';
      toast.error(errorMessage);
    }
  };

  const filteredAdmins = admins.filter((admin) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      admin.full_name.toLowerCase().includes(searchLower) ||
      admin.email.toLowerCase().includes(searchLower);

    return matchesSearch;
  });

  const getRoleBadge = (role: string) => {
    if (role === 'super_admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
          <ShieldCheck className="w-3 h-3" />
          Super Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <Shield className="w-3 h-3" />
        Admin
      </span>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="w-3 h-3" />
        Inactive
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Users</h1>
          <p className="text-gray-600">Manage administrator accounts and permissions</p>
        </div>

        {/* Stats - Only for super admin */}
        {isSuperAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Total Admins</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Active</div>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Super Admins</div>
              <div className="text-2xl font-bold text-primary-600">{stats.super_admins}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Regular Admins</div>
              <div className="text-2xl font-bold text-gray-600">{stats.admins}</div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Create Button - Only for super admin */}
            {isSuperAdmin && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <UserPlus className="w-5 h-5" />
                Create Admin
              </button>
            )}
          </div>
        </div>

        {/* Admins Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading admin users...</p>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="p-12 text-center">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No admin users found</p>
              {searchTerm && (
                <p className="text-sm text-gray-500">
                  Try adjusting your search or filters
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAdmins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {admin.full_name}
                            {admin.id === user?.id && (
                              <span className="ml-2 text-xs text-gray-500">(You)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{admin.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getRoleBadge(admin.role)}</td>
                      <td className="px-6 py-4">{getStatusBadge(admin.is_active)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(admin.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditingAdmin(admin)}
                            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Edit admin"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          {isSuperAdmin && admin.id !== user?.id && (
                            <button
                              onClick={() =>
                                handleDeleteAdmin(admin.id, admin.full_name)
                              }
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete admin"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results count */}
        {!loading && filteredAdmins.length > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredAdmins.length} of {admins.length} admin
            {admins.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateAdminModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          fetchAdmins();
          if (isSuperAdmin) {
            fetchStats();
          }
        }}
      />

      <EditAdminModal
        isOpen={!!editingAdmin}
        onClose={() => setEditingAdmin(null)}
        onSuccess={() => {
          fetchAdmins();
          if (isSuperAdmin) {
            fetchStats();
          }
        }}
        admin={editingAdmin}
        currentUserRole={user?.role || 'member'}
        currentUserId={user?.id || 0}
      />
    </div>
  );
};

export default AdminUsersPage;
